import type {ReactElement} from 'react';
import React, {useState} from 'react';
import type {VideoItem} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {VideoSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/video-slide-item.class';
import {ContextualMenuMediaControlOptions} from '@Components/contextual-menu/components/contextual-menu-media-control-options';
import type {MediaControlsItemProps} from '@Components/contextual-menu/components/media-controls-item';
import {onClickRemoveVideoBG, onOpenReplaceMedia, onOpenTrimVideoModal, onOpenVideoCropModal} from '@Components/poster-editor/library/poster-editor-open-modals';
import type {ItemData} from '@PosterWhiteboard/page/add-item.class';
import type {ReplaceMediaControlsButtons} from '@Components/replace-media-controls';
import {ContextualMenuReplaceMediaControls} from '@Components/contextual-menu/components/contextual-menu-replace-media-controls';
import {RemoveVideoBackgroundNotificationTag} from '@Components/poster-editor/components/remove-video-background-notification-tag';
import styles from './base-video-media-controls.module.scss';
import {GA4EventName, GA4EventParamName, GA4EventSources, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {useGetTeamCredits} from '@Hooks/useGetTeamCredits';

interface BaseVideoMediaControlsProps {
  activeItem: VideoItem | VideoSlideItem;

  onReplaceVideo(itemData: ItemData): void;

  onUnMute(): void;
}

enum VideoMediaControlsOptions {
  CROP = 'crop',
  TRIM = 'trim',
  REPLACE = 'replace',
  REMOVE_BG = 'remove-bg',
  UNMUTE = 'unmute',
}

export function BaseVideoMediaControls({...props}: BaseVideoMediaControlsProps): ReactElement {
  const [isReplaceOptionEnabled, setIsReplaceOptionEnabled] = useState(false);
  const credits = useGetTeamCredits() ?? 0;
  String(credits);

  const onCropVideo = (): void => {
    onOpenVideoCropModal(props.activeItem);
  };

  const onTrimVideo = (): void => {
    onOpenTrimVideoModal(props.activeItem);
  };

  const onReplaceCallback = (id: ReplaceMediaControlsButtons): void => {
    onOpenReplaceMedia(id, props.activeItem, (itemData: ItemData): void => {
      props.onReplaceVideo(itemData);
    });
  };

  const onClickRemoveBG = async (): Promise<void> => {
    await onClickRemoveVideoBG(props.activeItem, credits);
    trackPosterBuilderGA4Events(GA4EventName.REMOVE_BACKGROUND_VIDEO, {[GA4EventParamName.SOURCE]: GA4EventSources.MOBILE_BOTTOM_BAR});
  };

  const getItems = (): MediaControlsItemProps[] => {
    return [
      {
        id: `${VideoMediaControlsOptions.CROP}-video-controls`,
        icon: 'icon-crop',
        text: window.i18next.t('pmwjs_crop'),
        onClick: onCropVideo,
      },
      {
        id: `${VideoMediaControlsOptions.REPLACE}-video-controls`,
        icon: 'icon-replace',
        text: window.i18next.t('pmwjs_replace'),
        onClick: (): void => {
          setIsReplaceOptionEnabled(true);
        },
      },
      {
        id: `${VideoMediaControlsOptions.REMOVE_BG}-media-control`,
        icon: 'icon-remove-bg',
        text: window.i18next.t('pmwjs_ai_background_remover'),
        isLoading: props.activeItem.removeBackground.isBackgroundBeingRemoved,
        loadingText: window.i18next.t('pmwjs_removing_background'),
        tag: (
          <RemoveVideoBackgroundNotificationTag
            videoHashedFilename={props.activeItem.hashedFilename}
            startTime={props.activeItem.getStartTimeForNonRemovedBgVideo()}
            endTime={props.activeItem.getEndTimeForNonRemovedBgVideo()}
          />
        ),
        isSelected: props.activeItem.removeBackground.isBackgroundRemoved && !props.activeItem.removeBackground.isBackgroundBeingRemoved,
        onClick: (): void => {
          void onClickRemoveBG();
        },
        textPaddingClasses: 'spacing-p-r-4 spacing-p-l-4',
        tagContainerClassName: '_full-width',
      },
      {
        id: `${VideoMediaControlsOptions.TRIM}-video-controls`,
        icon: 'icon-trim-video',
        text: window.i18next.t('pmwjs_trim'),
        onClick: onTrimVideo,
      },
    ];
  };

  return isReplaceOptionEnabled ? (
    <ContextualMenuReplaceMediaControls
      id="background-image-replace-media-controls"
      controlsClassName={styles.replaceOption}
      onClick={onReplaceCallback}
      onCancel={(): void => {
        setIsReplaceOptionEnabled(false);
      }}
    />
  ) : (
    <ContextualMenuMediaControlOptions id="video-media-controls" items={getItems()} />
  );
}